export const RESTAURANTS_API_BASE_URL =
  process.env.NEXT_PUBLIC_RESTAURANTS_API_URL || '';

export const RESTAURANT_NO_IMAGE_URL = 'https://test-bookitplease.optimiser.site/image/no-image.png';

// resOS image CDN host. `NEXT_PUBLIC_RESOS_CDN_BASE` wins when set;
// falls back to the public imgix endpoint resOS documents in its
// partner API so restaurant images render even when the env var is
// missing (fresh checkouts, first-time deploys, etc.).
export const RESOS_CDN_BASE =
  process.env.NEXT_PUBLIC_RESOS_CDN_BASE || 'https://resos.imgix.net';

/**
 * Single source of truth for restaurant image URL resolution. Applied
 * everywhere a restaurant image is rendered (listing card, hero
 * autocomplete, detail page, table-selection summary, checkout order
 * summary, confirmation summary).
 *
 *   • Empty / null / undefined  → `https://test-bookitplease.optimiser.site/image/no-image.png` placeholder.
 *   • Absolute URL (http/https) → passed through unchanged.
 *   • Relative resOS path       → prepended with `RESOS_CDN_BASE`,
 *                                 with a slash guard so we never emit
 *                                 `://path` or a double-slash.
 *
 * Companion runtime guard (below) fires when the URL resolves but the
 * fetch 404s — swaps `.src` to the same placeholder so `<img>` never
 * shows a broken-image icon in production.
 */
export function resolveRestaurantImage(url: string | null | undefined): string {
  if (!url) return RESTAURANT_NO_IMAGE_URL;
  if (url.startsWith('http')) return url;
  return `${RESOS_CDN_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * `<img onError={...}>` handler that flips `.src` to the local
 * `no-image.png` placeholder when the underlying URL fails to load.
 * Self-clears `onerror` after the first swap so a load failure on
 * the placeholder itself can't loop.
 *
 * Kept in `config.ts` (not in a React component) so server-rendered
 * pages can import it without pulling a "use client" boundary.
 */
export function restaurantImageOnError(
  e: import('react').SyntheticEvent<HTMLImageElement>,
): void {
  const el = e.currentTarget;
  const next = RESTAURANT_NO_IMAGE_URL;
  if (el.src === next || el.src.endsWith('/image/no-image.png')) return;
  el.onerror = null;
  el.src = next;
}

export const RESOS_DAY_LABELS: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

/** Convert JS getDay() (0=Sun) to resOS day name */
export function jsToResosDay(jsDay: number): string {
  return RESOS_DAY_LABELS[jsDay] ?? 'monday';
}

/** Clamp a raw rating to 0-5, return integer count of filled stars */
export function getStarCount(rating: number): number {
  return Math.round(Math.min(5, Math.max(0, rating)));
}

export function buildRestaurantUrl(
  countrySlug: string,
  stateSlug: string,
  citySlug: string,
  slug: string,
  restaurantId?: string | null,
  date?: string | null,
  people?: number | string | null,
  time?: string | null,
): string {
  const parts = [countrySlug, stateSlug, citySlug, slug].filter(Boolean);
  const base = `/restaurants/${parts.join('/')}`;
  const p = new URLSearchParams();
  if (restaurantId) p.set('restaurantId', restaurantId);
  if (date) p.set('date', date);
  if (people) p.set('people', String(people));
  if (time) p.set('time', time);
  const qs = p.toString();
  return qs ? `${base}?${qs}` : base;
}

// ─── Shared date formatting ─────────────────────────────────────────

const WEEKDAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Canonical restaurant date/datetime display format used across every
 * restaurants surface (detail, listing card, table-selection, checkout,
 * confirmation):
 *
 *   `Wednesday, 2 December 2026 – 20:00`   (with time, en-dash + 24-h)
 *   `Wednesday, 2 December 2026`           (no time)
 *
 * Accepts `YYYY-MM-DD` / ISO date strings or a `Date` object. The time
 * argument is forwarded verbatim if it already matches `HH:mm`; an empty
 * / undefined time omits the trailing "– HH:mm" tail. Hand-formatted
 * (not `toLocaleDateString`) so the comma after the weekday is consistent
 * across Node ICU builds.
 */
export function formatRestaurantDateTime(
  date: string | Date | null | undefined,
  time?: string | null,
): string {
  if (!date) return '';
  let d: Date;
  if (date instanceof Date) {
    d = date;
  } else {
    // ISO date alone needs the T00:00:00 suffix to avoid TZ shift in some browsers.
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T00:00:00` : date;
    d = new Date(iso);
  }
  if (Number.isNaN(d.getTime())) {
    return String(date) + (time ? ` – ${format12h(time)}` : '');
  }
  const datePart = `${WEEKDAY_NAMES[d.getDay()]}, ${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  const t = format12h((time || '').trim());
  return t ? `${datePart} – ${t}` : datePart;
}

/**
 * Convert a 24-hour `HH:mm` string to 12-hour `h:mm AM/PM`. Used to
 * present slot times in the customer-facing surfaces (confirmation
 * page, checkout summary, email templates) so `19:00` renders as
 * `7:00 PM` — matches what the server-side `formatReservationDateTime`
 * emits in booking-confirmation emails. Anything not shaped `HH:mm`
 * passes through unchanged (defensive for future free-text inputs).
 */
function format12h(time: string): string {
  const t = (time || '').trim();
  if (!t) return '';
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return t;
  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return t;
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
}

/**
 * Date-only variant of `formatRestaurantDateTime` — no weekday, no
 * time. Used by the confirmation page's "Booking date:" row where
 * design spec is `27 March 2026` rather than `Wednesday, 27 March
 * 2026`. Same input parsing as `formatRestaurantDateTime` so
 * `YYYY-MM-DD` / ISO / `Date` all resolve consistently.
 */
export function formatRestaurantDate(
  date: string | Date | null | undefined,
): string {
  if (!date) return '';
  let d: Date;
  if (date instanceof Date) {
    d = date;
  } else {
    const iso = /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T00:00:00` : date;
    d = new Date(iso);
  }
  if (Number.isNaN(d.getTime())) return String(date);
  return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Canonical duration display used across the restaurants booking flow
 * (table-selection, checkout, confirmation). Design spec: always render
 * in whole minutes — `90` -> `90 min`, `120` -> `120 min`, `45` ->
 * `45 min`. No hour formatting: the CMS team wanted the exact minute
 * count visible so customers can compare slot durations at a glance.
 */
export function formatRestaurantDuration(minutes: number | string | null | undefined): string {
  const mins = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
  if (!mins || Number.isNaN(mins) || mins <= 0) return '';
  return `${mins} min`;
}

/**
 * Compose `fromDateTime` / `toDateTime` (resOS expects
 * `YYYY-MM-DDTHH:mm:ss`) from a `YYYY-MM-DD` date + `HH:mm` time.
 * Shared by the booking widget (pre-navigation availability check) and
 * the table-selection page (its own available-tables fetch) so both
 * surfaces build the exact same window for the same date/time.
 */
export function buildRestaurantBookingWindow(
  date: string,
  time: string,
  windowMinutes = 120,
): { fromDateTime: string; toDateTime: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!/^\d{1,2}:\d{2}$/.test(time)) return null;
  const [hh, mm] = time.split(':').map((s) => parseInt(s, 10));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  const start = new Date(`${date}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start.getTime() + windowMinutes * 60 * 1000);
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dy = String(d.getDate()).padStart(2, '0');
    const hr = String(d.getHours()).padStart(2, '0');
    const mn = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${dy}T${hr}:${mn}:00`;
  };
  return { fromDateTime: fmt(start), toDateTime: fmt(end) };
}
